import {AfterViewInit, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {InfiniteScrollModule} from 'ngx-infinite-scroll';

import {Listing} from '../../../../core/models/listing';
import {MuskokaSoldService} from '../../services/muskoka-sold.service';
import {SEOService} from '../../../../core/services/seo.service';
import {NavigationComponent} from '../../../../shared/components/navigation/navigation.component';
import {MuskokaListingsSearchComponent} from '../../components/search-filter/muskoka-listings-search.component';
import {BaseDashboardComponent} from "../../../../shared/components/base-dashboard/base-dashboard.component";
import {LISTINGS_SERVICE_TOKEN} from "../../../../core/services/listings-service.token";


import {Globals} from '../../../../core/services/globals';

import {BsModalService} from 'ngx-bootstrap/modal';

import {TrackingService} from '../../../../core/services/tracking.service';
// GA4 Analytics Tracking
import {DataLayerService} from '../../../../core/services/data-layer.service';

import {NgbdCarouselConfigComponent} from '../../../../shared/components/carousel/carousel-config';
import {CommonModule, CurrencyPipe, DOCUMENT} from '@angular/common';
import {BreakpointObserver} from '@angular/cdk/layout';
import {DomSanitizer} from "@angular/platform-browser";
import {PropertyCardComponent} from "../../../../shared/components/property-card/property-card-component";

@Component({
  imports: [
    CommonModule,
    InfiniteScrollModule,
    MuskokaListingsSearchComponent,
    NavigationComponent,
    RouterModule,
    NgbdCarouselConfigComponent,
    PropertyCardComponent
  ],
  providers: [
    CurrencyPipe,  // <-- Add this if needed
    {provide: LISTINGS_SERVICE_TOKEN, useClass: MuskokaSoldService}
  ],
  standalone: true,
  selector: 'my-sold-dashboard',
  templateUrl: './sold-dashboard.component.html',
  styleUrls: ['./sold-dashboard.component.css'],
})

export class SoldDashboardComponent extends BaseDashboardComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(
    protected override router: Router,
    protected override activatedRoute: ActivatedRoute,
    protected override http: HttpClient,
    protected override renderer: Renderer2,
    protected override listingsService: MuskokaSoldService,
    protected override SEOService: SEOService,
    public override globals: Globals,
    protected override elementRef: ElementRef,
    protected override modalService: BsModalService,
    protected override dataLayerService: DataLayerService,
    protected override trackingService: TrackingService,
    protected override breakpointObserver: BreakpointObserver,
    protected override sanitizer: DomSanitizer,
    @Inject(DOCUMENT) protected override document: Document,
    protected override zone: NgZone
  ) {
    super(
      router,
      activatedRoute,
      http,
      renderer,
      listingsService,
      SEOService,
      globals,
      elementRef,
      modalService,
      dataLayerService,
      trackingService,
      breakpointObserver,
      sanitizer,
      document,
      zone
    );
  }

  ngOnInit(): void {
    console.log('ngOnInit: Dashboard');
    // Initialize the base dashboard functionality
    this.initializeBaseDashboard();

    // override the registration status to not require registration
    this.globals.isRegisteredBln = '1';

    // override the search min
    this.globals.searchMin = '0';

    // Detect screen changes
    this.setupBreakpointObserver();

    // Perform shared initialization tasks
    this.initializeSharedData();

    // load the initial page data
    this.initializePageContent();

    // Allow child classes to customize their behavior
    this.initDashboard();
  }


  ngAfterViewInit(): void {
    const header = document.querySelector('.navbar-default');
    if (header != null) {
      header.classList.remove('navbar-shrink');
    }
    setTimeout(() => this.setScrollTo('resultsList'));
  }


  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }


  protected override initDashboard(): void {
    console.log('init Sold Dashboard');
    console.log('Path: ' + this.router.url);
    this.setGlobalSearchMin();
    this.handleDistrictDefaults();
    // Reset the previousRouteStr on page load
    this.resetPreviousRoute();
    console.log('District Loading: ' + this.globals.districtList);
    // fork, if the listings haven't been already loaded then load them up
    if (this.globals.listings.length === 0) {
      this.fetchListings();
    } else {
      this.listings = this.globals.listings;
      this.selectedListing = this.globals.selectedListing;
      this.isSelectedBln = true;
    }

    return;
    // fork, if the user has registered then set the property variable
    if (this.globals.isRegisteredBln === '1') {
      this.isRegisteredBln = '1';
    }
    // disable the registration requirment
    // this.isRegisteredBln = true;
    // console.log('Registration Status: ' + this.isRegisteredBln);
  }

  protected override onListingsFetched(listings: Listing[]): void {
    // Map listings to include banner property
    this.listings = listings.map((listing) => {
      const banner = this.getBannerClass(listing); // Compute banner object
      //console.log('Banner for listing:', listing.id, banner); // Debug banner logic
      return {
        ...listing,   // Include all the original properties
        banner,       // Add the banner property
      };
    });

    // Assign the updated list to the global variable
    this.globals.listings = this.listings;

    // Debugging all mutated listings
    //console.log('Listings fetched with banners:', this.listings);

    // Navigate to the first listing if it exists
    if (this.listings && this.listings.length > 0) {
      this.gotoDetail(this.listings[0]); // Navigate to the first listing
    } else {
      console.warn('No listings found.');
    }
  }


  protected getBannerClass(listing: Listing): { bannerClass: string, lineClass: string, text: string } | null {
    const listingStatus = (listing.listingStatus || '').toLowerCase();
    const bannerOverride = listing.listingDetail?.bannerOverride || '';

    if (listingStatus === 'sold' && !listing.exclusive) {
      return {bannerClass: 'sold', lineClass: 'line', text: '<span>Sold</span>'};
    }

    if (listing.exclusive && listingStatus !== 'sold') {
      return {bannerClass: 'exclusive', lineClass: 'line', text: '<span>Exclusive</span>'};
    }

    if (listing.exclusive && listingStatus === 'sold') {
      const lineClass = this.currentScreenSize !== 'XLarge' ? 'line-small' : 'line';

      return {
        bannerClass: 'banner-exclusive-sold sold',
        lineClass: lineClass,
        text: '<span>Sold - Exclusively listed<br/> by Cayman Marshall</span>',
      };
    }

    if (listing.isModified && !listing.isNew && !listing.isPriceChange && listingStatus !== 'sold' && bannerOverride === '') {
      return {bannerClass: 'change', lineClass: 'line', text: '<span>Modified</span>'};
    }

    if (listing.isModified && !listing.isNew && listing.isPriceChange && bannerOverride === '') {
      return {bannerClass: 'change', lineClass: 'line', text: '<span>Price change</span>'};
    }

    if (listing.isNew && listingStatus !== 'sold' && bannerOverride === '') {
      return {bannerClass: 'new', lineClass: 'line', text: '<span>New</span>'};
    }

    if ((listingStatus === 'rental' || listingStatus === 'active') && bannerOverride !== '' && bannerOverride !== 'n/a') {
      const isLineSmall = bannerOverride.length > 20; // Use `line-small` for longer texts
      return {bannerClass: 'rental', lineClass: isLineSmall ? 'line-small' : 'line', text: '<span>' + bannerOverride + '</span>'};
    }

    return null; // No banner
  }


}
